import { useEffect, useState } from "react";
import { FileSpreadsheet, Printer } from "lucide-react";
import api, { downloadExcel } from "../api";
import StatusBadge from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { fmtDate, fmtMoney } from "../utils/format";

const TYPES = [
  ["ventes", "Ventes"], ["locations", "Locations"], ["depenses", "Dépenses"],
  ["credits", "Crédits clients"], ["vehicules", "Véhicules en stock"],
  ["transactions", "Caisse & Banque"], ["rentabilite", "Rentabilité par véhicule"],
  ["stock", "Aging du stock"], ["fournisseurs", "Fournisseurs"], ["reservations", "Réservations"],
];

const MONEY_KEYS = ["final_price", "paid_amount", "remaining", "amount_fcfa", "amount", "total", "paid", "purchase_price_fcfa", "total_expenses", "cost_basis", "sale_price", "profit", "rental_revenue", "total_amount"];
const DATE_KEYS = ["sale_date", "date", "due_date", "start_datetime", "expected_return"];
const STATUS_KEYS = ["status"];

export default function Reports() {
  const [type, setType] = useState("ventes");
  const [dates, setDates] = useState({ date_from: "", date_to: "" });
  const [data, setData] = useState(null);

  const load = () => {
    const p = new URLSearchParams({ type });
    if (dates.date_from) p.set("date_from", dates.date_from);
    if (dates.date_to) p.set("date_to", dates.date_to);
    api.get(`/reports/data?${p}`).then((r) => setData(r.data)).catch(() => setData(null));
  };
  useEffect(() => { load(); }, [type, dates]);

  const renderCell = (col, row) => {
    const v = row[col.key];
    if (MONEY_KEYS.includes(col.key)) return <span className="font-mono-num">{fmtMoney(v)}</span>;
    if (DATE_KEYS.includes(col.key)) return fmtDate(v);
    if (STATUS_KEYS.includes(col.key)) return <StatusBadge status={v} />;
    if (col.key === "margin_pct") return v != null ? `${v} %` : "—";
    if (col.key === "direction") return v === "in" ? "Entrée" : "Sortie";
    return String(v ?? "—");
  };

  return (
    <div className="space-y-5" data-testid="reports-page">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Rapports</h1>
        <p className="mt-1 text-sm text-slate-500">Filtrez par période et exportez en Excel ou imprimez en PDF.</p>
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-slate-200 bg-white p-4">
        <div><Label className="text-xs">Type de rapport</Label>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="w-64" data-testid="report-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>{TYPES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs">Du</Label><Input type="date" value={dates.date_from} onChange={(e) => setDates({ ...dates, date_from: e.target.value })} data-testid="report-from" /></div>
        <div><Label className="text-xs">Au</Label><Input type="date" value={dates.date_to} onChange={(e) => setDates({ ...dates, date_to: e.target.value })} data-testid="report-to" /></div>
        <Button variant="outline" data-testid="report-export-excel"
          onClick={() => downloadExcel(`/export/excel?type=${type}${dates.date_from ? `&date_from=${dates.date_from}` : ""}${dates.date_to ? `&date_to=${dates.date_to}` : ""}`)}>
          <FileSpreadsheet className="mr-1 h-4 w-4" /> Export Excel
        </Button>
        <Button variant="outline" onClick={() => window.print()} data-testid="report-print"><Printer className="mr-1 h-4 w-4" /> Imprimer / PDF</Button>
      </div>

      {data && (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="report-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-slate-50 text-left">
                {data.columns.map((c) => <th key={c.key} className="whitespace-nowrap px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">{c.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {data.rows.length === 0 && <tr><td colSpan={data.columns.length} className="px-4 py-10 text-center text-slate-400">Aucune donnée pour ces critères.</td></tr>}
              {data.rows.map((r, i) => (
                <tr key={r.id || i} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/60">
                  {data.columns.map((c) => <td key={c.key} className="px-4 py-2.5">{renderCell(c, r)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
          <p className="border-t px-4 py-2 text-xs text-slate-400">{data.rows.length} ligne(s)</p>
        </div>
      )}
    </div>
  );
}
